import DurationField from "@/components/blocks/DurationField"
import FormRow from "@/components/blocks/FormRow"
import { className } from "@/helpers/className"
import { resetSpecificFieldData } from "@/helpers/forms"
import useViewport from "@/hooks/useViewport"
import React, { useCallback, useMemo } from "react"
import { Col, Form, Row } from "react-bootstrap"
import { Form as FForm, Field } from "react-final-form"
import { DateRangeFields, LocationFields, SchoolField, SubjectField } from "@components/fields"
import { SendReportFields, EducatableField, ScheduleFormField as FormField } from "../fields"
import { FormFieldNames, fieldViewProps } from "./constants"
import { mapFormFieldsToRequestFields } from "./utils"
import MultipleValuesSetter from "./components/MultipleValuesSetter"
import { isSubmitButtonDisabled } from "@/helpers/validators"

const defaultLabelProps = { xs: 24, lg: 8, className: "v-2 col-form-label px-0" }

export const ScheduleForm = React.memo(
  ({
    initialValues,
    multipleValues,
    validate,
    loading,
    disabled,
    type = "create",
    onSubmit,
    onCancel,
    dateRangeRequired = true,
    children,
    canManageTranscriptions = false,
  }) => {
    const { isMobileViewport } = useViewport()

    const submit = useCallback(values => onSubmit(mapFormFieldsToRequestFields(values)), [onSubmit])

    const labelProps = useMemo(
      () => ({
        ...defaultLabelProps,
        className: className(defaultLabelProps.className, disabled && "text-gray"),
      }),
      [disabled]
    )

    const dateLabelProps = useMemo(
      () => ({
        ...labelProps,
        className: className(labelProps.className, !isMobileViewport && "mt-2 pt-4"),
      }),
      [isMobileViewport, labelProps]
    )

    const renderFormFields = useCallback(
      ({ handleSubmit, values, pristine, hasValidationErrors }) => (
        <Row as="form" onSubmit={handleSubmit}>
          <Col xs={24} lg={type === "create" ? 18 : 24} xl={type === "create" ? 12 : 24}>
            {type === "create" && (
              <>
                <FormRow label="School" {...fieldViewProps} LabelProps={defaultLabelProps}>
                  <SchoolField name={FormFieldNames.School} />
                </FormRow>
                <FormRow label="Student or group" {...fieldViewProps} LabelProps={defaultLabelProps}>
                  <EducatableField
                    disabled={!values[FormFieldNames.School]?.title}
                    school={values[FormFieldNames.School]}
                  />
                </FormRow>
              </>
            )}

            <FormRow label="Subject" {...fieldViewProps} LabelProps={defaultLabelProps}>
              <SubjectField name={FormFieldNames.Subject} multipleValues={multipleValues[FormFieldNames.Subject]} />
              {children}
            </FormRow>

            <FormRow label="Date range" {...fieldViewProps} LabelProps={dateLabelProps}>
              <DateRangeFields
                disabled={disabled}
                startPlaceholder={multipleValues[FormFieldNames.StartOn] ? "Multiple values" : "Date"}
                endPlaceholder={multipleValues[FormFieldNames.EndOn] ? "Multiple values" : "Date"}
                startRequired={dateRangeRequired}
                endRequired={dateRangeRequired}
              />
            </FormRow>

            <FormRow label="Default location" {...fieldViewProps} LabelProps={labelProps}>
              <MultipleValuesSetter initial={!multipleValues[FormFieldNames.MeetingFormat]}>
                <LocationFields disabled={disabled} multipleValues={multipleValues} />
              </MultipleValuesSetter>
            </FormRow>

            <FormRow {...fieldViewProps} LabelProps={labelProps}>
              {{
                label: (
                  <Form.Label column {...labelProps}>
                    <Row>
                      <Col as="span" xs="auto" lg={24}>
                        Schedule note
                      </Col>
                      <Col as="span" xs="auto" lg={24} className="text-gray font-weight-normal">
                        Visible to tutors
                      </Col>
                    </Row>
                  </Form.Label>
                ),
                col: (
                  <FormField
                    name={FormFieldNames.ScheduleNote}
                    as="editor"
                    rows="3"
                    disabled={disabled}
                    multipleValues={multipleValues[FormFieldNames.ScheduleNote]}
                  />
                ),
              }}
            </FormRow>

            <hr />

            <FormRow label="Maximum sessions" {...fieldViewProps} LabelProps={labelProps}>
              <Row>
                <Col xs={24} lg={7}>
                  <FormField
                    name={FormFieldNames.MaxSessions}
                    type="number"
                    placeholder="10"
                    disabled={disabled}
                    multipleValues={multipleValues[FormFieldNames.MaxSessions]}
                  />
                </Col>
              </Row>
            </FormRow>

            <FormRow label="Max session duration" {...fieldViewProps} LabelProps={labelProps}>
              <Row>
                <Col xs={24} lg={7}>
                  <Field name={FormFieldNames.Duration}>
                    {({ input }) => (
                      <DurationField
                        {...input}
                        className="v-2"
                        placeholder={multipleValues[FormFieldNames.Duration] ? "Multiple values" : "Duration"}
                        disabled={disabled}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
            </FormRow>

            <FormRow label="School rate" {...fieldViewProps} LabelProps={labelProps}>
              <Row>
                <Col xs={24} lg={7}>
                  <FormField
                    name={FormFieldNames.Rate}
                    type="number"
                    placeholder="10"
                    disabled={disabled}
                    multipleValues={multipleValues[FormFieldNames.Rate]}
                  />
                </Col>
              </Row>
            </FormRow>

            <FormRow label="Send reports to" {...fieldViewProps} LabelProps={labelProps}>
              <SendReportFields disabled={disabled} multipleValues={multipleValues} />
            </FormRow>

            <FormRow label="Report" {...fieldViewProps} LabelProps={labelProps}>
              <span className={className("py-2 my-1", disabled && "text-gray")}>Weekly</span>
            </FormRow>

            <FormRow label="Status" {...fieldViewProps} LabelProps={labelProps}>
              <span className={className("py-2 my-1", disabled && "text-gray")}>Active</span>
            </FormRow>

            {/* Only show transcription field if user can manage transcription */}
            {canManageTranscriptions && (
              <FormRow label="Transcription" {...fieldViewProps} LabelProps={labelProps}>
                <MultipleValuesSetter initial={!multipleValues[FormFieldNames.EnableTranscription]}>
                  <Row className="align-items-center py-2">
                    <Col xs={24} lg="auto" className={isMobileViewport ? "mb-2" : ""}>
                      <Field name={FormFieldNames.EnableTranscription}>
                        {({ input }) => (
                          <Form.Check
                            type="radio"
                            id="transcription-enabled"
                            className="text-nowrap mr-2 v-2"
                            label="Enabled"
                            custom
                            disabled={disabled}
                            checked={input.value === true}
                            onChange={() => input.onChange(true)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col xs={24} lg="auto">
                      <Field name={FormFieldNames.EnableTranscription}>
                        {({ input }) => (
                          <Form.Check
                            type="radio"
                            id="transcription-disabled"
                            className="text-nowrap mr-2 v-2"
                            label="Disabled"
                            custom
                            disabled={disabled}
                            checked={input.value === false}
                            onChange={() => input.onChange(false)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                </MultipleValuesSetter>
              </FormRow>
            )}

            <hr />

            <Row
              className={`align-items-center gap-2 ${
                type === "create" ? "justify-content-start" : "justify-content-end"
              }`}
            >
              {onCancel && (
                <button className="btn btn-link text-black text-no-wrap" type="button" onClick={onCancel}>
                  Cancel
                </button>
              )}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isSubmitButtonDisabled({ disabled, pristine, hasValidationErrors, loading })}
              >
                {type === "create" ? "Create" : "Save"}
              </button>
            </Row>
          </Col>
        </Row>
      ),
      [
        type,
        disabled,
        loading,
        onCancel,
        children,
        labelProps,
        dateLabelProps,
        multipleValues,
        dateRangeRequired,
        isMobileViewport,
        canManageTranscriptions,
      ]
    )

    return (
      <FForm
        initialValues={initialValues}
        validate={validate}
        onSubmit={submit}
        mutators={{ resetEducatable: resetSpecificFieldData({ name: FormFieldNames.Educatable, value: null }) }}
      >
        {renderFormFields}
      </FForm>
    )
  }
)

ScheduleForm.displayName = "ScheduleForm"
